import React, { useRef } from 'react';
import './SubwayMap.css';

const SubwayMap = ({ steps, connections, title, variant = 'reference', referenceWorkflow = null }) => {
  const scale = 1;
  const offsetX = 60;
  const offsetY = 100;
  const stationRadius = 14;
  const lineWidth = 6;

  // Generate unique IDs for gradients (stable per component instance)
  const idRef = useRef(Math.random().toString(36).substring(2, 11));
  const lineId = `subway-line-${variant}-${idRef.current}`;
  const arrowId = `arrowhead-${variant}-${idRef.current}`;

  // Calculate dynamic viewBox based on steps
  const minX = Math.min(...steps.map(s => s.x)) * scale + offsetX - 100; // Add left padding for text labels
  const maxX = Math.max(...steps.map(s => s.x)) * scale + offsetX + 150;
  const maxY = Math.max(...steps.map(s => Math.abs(s.y))) * scale + offsetY + 45;
  const minY = Math.min(...steps.map(s => s.y)) * scale + offsetY - 30;

  // Check if a step exists in the reference workflow
  const stepExistsInReference = (stepName, reference) => {
    if (!reference || !reference.steps) return false;
    return reference.steps.some(s => s.name === stepName);
  };

  // Check if a connection matches the reference workflow
  const doesConnectionMatchReference = (connection, currentSteps, reference) => {
    if (!reference || !reference.steps || !reference.connections) return false;
    
    const fromStep = currentSteps.find(s => s.id === connection.from);
    const toStep = currentSteps.find(s => s.id === connection.to);
    if (!fromStep || !toStep) return false;
    
    // Both steps must exist in reference
    if (!stepExistsInReference(fromStep.name, reference) || 
        !stepExistsInReference(toStep.name, reference)) {
      return false;
    }
    
    // Find matching steps in reference by name
    const refFromStep = reference.steps.find(s => s.name === fromStep.name);
    const refToStep = reference.steps.find(s => s.name === toStep.name);
    
    if (!refFromStep || !refToStep) return false;
    
    // Check if this exact connection exists in reference
    const matchingConnection = reference.connections.find(
      conn => conn.from === refFromStep.id && conn.to === refToStep.id
    );
    
    return !!matchingConnection;
  };

  // Color scheme: green for reference/matching, red for differences
  const getLineColor = (connection) => {
    if (variant === 'reference') {
      return '#198754'; // Green for reference
    }
    
    // For detected workflows, compare with reference
    if (referenceWorkflow) {
      const matches = doesConnectionMatchReference(connection, steps, referenceWorkflow);
      return matches ? '#198754' : '#dc3545'; // Green if matches, red if differs
    }
    
    return '#dc3545'; // Default red for detected
  };

  // Get step color (for station circles)
  const getStepColor = (stepName) => {
    if (variant === 'reference') {
      return '#198754'; // Green for reference
    }
    
    // For detected workflows, check if step exists in reference
    if (referenceWorkflow) {
      return stepExistsInReference(stepName, referenceWorkflow) ? '#198754' : '#dc3545';
    }
    
    return '#dc3545'; // Default red for detected
  };

  return (
    <div className={`subway-map-container ${variant}`}>
      {title && <h5 className="mb-1 fw-semibold" style={{fontSize: '1rem'}}>{title}</h5>}
      <div className="subway-map-wrapper">
        <svg 
          className="subway-map-svg"
          viewBox={`${minX} ${minY - 15} ${maxX - minX + 40} ${maxY - minY + 30}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            {/* Arrow marker definitions for green and red */}
            <marker
              id={`${arrowId}-green`}
              markerWidth="7"
              markerHeight="7"
              refX="6"
              refY="3.5"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path
                d="M 0 0 L 7 3.5 L 0 7 Z"
                fill="#198754"
              />
            </marker>
            <marker
              id={`${arrowId}-red`}
              markerWidth="7"
              markerHeight="7"
              refX="6"
              refY="3.5"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path
                d="M 0 0 L 7 3.5 L 0 7 Z"
                fill="#dc3545"
              />
            </marker>
          </defs>

          {/* Draw subway lines (tracks) first - behind stations */}
          {connections.map((conn, idx) => {
            const fromStep = steps.find(s => s.id === conn.from);
            const toStep = steps.find(s => s.id === conn.to);
            if (!fromStep || !toStep) return null;

            const fromCenterX = fromStep.x * scale + offsetX;
            const fromCenterY = fromStep.y * scale + offsetY;
            const toCenterX = toStep.x * scale + offsetX;
            const toCenterY = toStep.y * scale + offsetY;

            // Calculate direction vector
            const dx = toCenterX - fromCenterX;
            const dy = toCenterY - fromCenterY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // Calculate unit vector
            const unitX = dx / distance;
            const unitY = dy / distance;
            
            // Calculate edge points (where line touches circle edges)
            const fromX = fromCenterX + unitX * stationRadius;
            const fromY = fromCenterY + unitY * stationRadius;
            const toX = toCenterX - unitX * stationRadius;
            const toY = toCenterY - unitY * stationRadius;

            // Get color for this connection
            const connColor = getLineColor(conn);
            const arrowMarkerId = connColor === '#198754' ? `${arrowId}-green` : `${arrowId}-red`;

            // Use smooth curves for all connections (no right angles)
            const isDiagonal = Math.abs(dy) > 5 && Math.abs(dx) > 5;
            
            if (isDiagonal) {
              // Use smooth quadratic curve for diagonal connections
              const controlX = (fromX + toX) / 2;
              const controlY = (fromY + toY) / 2;
              // Add some curve to make it smooth
              const curveOffset = Math.min(Math.abs(dx), Math.abs(dy)) * 0.3;
              const finalControlX = controlX;
              const finalControlY = fromY < toY ? controlY - curveOffset : controlY + curveOffset;
              
              return (
                <g key={`conn-${idx}`}>
                  {/* Thick colored line (the track) */}
                  <path
                    d={`M ${fromX} ${fromY} Q ${finalControlX} ${finalControlY} ${toX} ${toY}`}
                    className="subway-track"
                    stroke={connColor}
                    strokeWidth={lineWidth}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Arrow on the line */}
                  <path
                    d={`M ${fromX} ${fromY} Q ${finalControlX} ${finalControlY} ${toX} ${toY}`}
                    className="subway-arrow-line"
                    stroke={connColor}
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    markerEnd={`url(#${arrowMarkerId})`}
                  />
                </g>
              );
            } else {
              // Straight horizontal or vertical line
              return (
                <g key={`conn-${idx}`}>
                  {/* Thick colored line (the track) */}
                  <line
                    x1={fromX}
                    y1={fromY}
                    x2={toX}
                    y2={toY}
                    className="subway-track"
                    stroke={connColor}
                    strokeWidth={lineWidth}
                    strokeLinecap="round"
                  />
                  {/* Arrow on the line */}
                  <line
                    x1={fromX}
                    y1={fromY}
                    x2={toX}
                    y2={toY}
                    className="subway-arrow-line"
                    stroke={connColor}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    markerEnd={`url(#${arrowMarkerId})`}
                  />
                </g>
              );
            }
          })}

          {/* Draw stations (circular nodes) */}
          {steps.map((step, index) => {
            const x = step.x * scale + offsetX;
            const y = step.y * scale + offsetY;
            const stepColor = getStepColor(step.name);

            return (
              <g key={step.id} className="subway-station-group">
                {/* White circle (station) on top of the line */}
                <circle
                  cx={x}
                  cy={y}
                  r={stationRadius}
                  className={`subway-station subway-station-${variant}`}
                  fill="#ffffff"
                  stroke={stepColor}
                  strokeWidth="3"
                />
                
                {/* Station label - positioned below or to the side */}
                <text
                  x={x}
                  y={y + stationRadius + 18}
                  className="subway-station-label"
                  textAnchor="middle"
                  fill="#212529"
                >
                  {step.name}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

export default SubwayMap;

